'use client';

import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { normalizeColorText } from '@/lib/product-options/color-option';

export type ColorComboboxItem = {
  value: string;
  label: string;
  /** CSS `background` voor de staal (kleur of verloop). */
  background: string;
  /** Genormaliseerde zoektekst (label, leveranciersnaam, waarde). */
  searchText: string;
  disabled?: boolean;
  /** Extra regel onder het label, bijvoorbeeld een meerprijs. */
  note?: string;
};

const COPY = {
  nl: { search: 'Zoek een kleur', empty: 'Geen kleur gevonden', count: (n: number) => `${n} kleuren`, choose: 'Kies een kleur' },
  en: { search: 'Search for a colour', empty: 'No colour found', count: (n: number) => `${n} colours`, choose: 'Choose a colour' },
  de: { search: 'Farbe suchen', empty: 'Keine Farbe gefunden', count: (n: number) => `${n} Farben`, choose: 'Farbe wählen' },
  fr: { search: 'Rechercher une couleur', empty: 'Aucune couleur trouvée', count: (n: number) => `${n} couleurs`, choose: 'Choisir une couleur' },
} as const;

function copyFor(locale: string) {
  const language = String(locale || 'nl').split('-')[0] as keyof typeof COPY;
  return COPY[language] || COPY.en;
}

export function ColorSwatchDot({ background, size = 'md' }: { background: string; size?: 'sm' | 'md' | 'lg' }) {
  const dimension = size === 'sm' ? 'h-5 w-5' : size === 'lg' ? 'h-7 w-7' : 'h-6 w-6';
  return (
    <span
      className={`${dimension} flex-none rounded-full border border-gray-300 shadow-inner`}
      style={{ background }}
      aria-hidden="true"
    />
  );
}

/**
 * Kleurkiezer voor opties met veel kleuren: een knop met de gekozen staal en
 * daaronder een doorzoekbare lijst met een staal per kleur.
 *
 * Bewust zonder portal: het paneel staat in de documentstroom onder de knop,
 * zodat het niet wordt afgesneden door een kaart met overflow en een
 * vergrendelde optie (inert) ook het paneel vergrendelt. Op mobiel schuift de
 * rest van het formulier gewoon mee naar beneden.
 */
export function ColorOptionCombobox({
  items,
  selectedValue,
  onSelect,
  locale,
  ariaLabel,
  placeholder,
}: {
  items: ColorComboboxItem[];
  selectedValue: string;
  onSelect: (value: string) => void;
  locale: string;
  ariaLabel: string;
  placeholder?: string;
}) {
  const copy = copyFor(locale);
  const baseId = useId();
  const listId = `${baseId}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = items.find((item) => item.value === selectedValue) || null;
  const filterItems = (search: string) => {
    const terms = normalizeColorText(search).split(' ').filter(Boolean);
    if (!terms.length) return items;
    return items.filter((item) => terms.every((term) => item.searchText.includes(term)));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filtered = useMemo(() => filterItems(query), [items, query]);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const node = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const openPanel = (focusSearch: boolean) => {
    const selectedIndex = items.findIndex((item) => item.value === selectedValue && !item.disabled);
    setQuery('');
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : items.findIndex((item) => !item.disabled));
    setOpen(true);
    // Op een touchscreen geen toetsenbord opdringen; de lijst is al scrollbaar.
    const finePointer = typeof window !== 'undefined' && window.matchMedia?.('(pointer: fine)').matches;
    requestAnimationFrame(() => {
      if (focusSearch || finePointer) inputRef.current?.focus();
      else listRef.current?.focus();
    });
  };

  const closePanel = (returnFocus = true) => {
    setOpen(false);
    if (returnFocus) requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const choose = (item: ColorComboboxItem) => {
    if (item.disabled) return;
    onSelect(item.value);
    closePanel();
  };

  const move = (direction: 1 | -1, from = activeIndex) => {
    if (!filtered.length) return;
    let index = from;
    for (let step = 0; step < filtered.length; step += 1) {
      index = (index + direction + filtered.length) % filtered.length;
      if (!filtered[index].disabled) {
        setActiveIndex(index);
        return;
      }
    }
  };

  const onListKeyDown = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        move(-1);
        break;
      case 'Home':
        if (event.target === inputRef.current) return;
        event.preventDefault();
        move(1, -1);
        break;
      case 'End':
        if (event.target === inputRef.current) return;
        event.preventDefault();
        move(-1, filtered.length);
        break;
      case 'Enter':
        event.preventDefault();
        if (activeIndex >= 0 && filtered[activeIndex]) choose(filtered[activeIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        closePanel();
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
    }
  };

  const activeId = open && activeIndex >= 0 && filtered[activeIndex] ? `${baseId}-option-${activeIndex}` : undefined;

  return (
    <div ref={rootRef} className="w-full">
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={selected ? `${ariaLabel}: ${selected.label}` : ariaLabel}
        onClick={() => (open ? closePanel(false) : openPanel(false))}
        onKeyDown={(event) => {
          if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
            event.preventDefault();
            openPanel(true);
          }
        }}
        className="flex min-h-12 w-full items-center gap-3 rounded-md border border-input bg-transparent px-3 py-2 text-left text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        {selected ? <ColorSwatchDot background={selected.background} /> : null}
        <span className={`min-w-0 flex-1 truncate ${selected ? 'text-gray-900' : 'text-muted-foreground'}`}>
          {selected ? selected.label : placeholder || copy.choose}
        </span>
        <span className="hidden flex-none text-xs text-gray-500 sm:inline">{copy.count(items.length)}</span>
        <ChevronDown className={`h-4 w-4 flex-none opacity-50 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3">
            <Search className="h-4 w-4 flex-none text-gray-400" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              role="searchbox"
              value={query}
              onChange={(event) => {
                const next = event.target.value;
                setQuery(next);
                setActiveIndex(filterItems(next).findIndex((item) => !item.disabled));
              }}
              onKeyDown={onListKeyDown}
              placeholder={copy.search}
              aria-label={copy.search}
              aria-controls={listId}
              aria-activedescendant={activeId}
              autoComplete="off"
              spellCheck={false}
              className="h-11 w-full min-w-0 bg-transparent text-base outline-none placeholder:text-gray-400 sm:text-sm"
            />
          </div>
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            aria-activedescendant={activeId}
            tabIndex={-1}
            onKeyDown={onListKeyDown}
            className="max-h-72 overflow-y-auto overscroll-contain p-1 outline-none sm:grid sm:grid-cols-2 sm:gap-x-1"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-sm text-gray-500 sm:col-span-2" role="presentation">
                {copy.empty}
              </li>
            ) : (
              filtered.map((item, index) => {
                const isSelected = item.value === selectedValue;
                const isActive = index === activeIndex;
                return (
                  <li
                    key={item.value}
                    id={`${baseId}-option-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={item.disabled || undefined}
                    onPointerMove={() => !item.disabled && setActiveIndex(index)}
                    onClick={() => choose(item)}
                    className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2.5 py-1.5 text-sm ${
                      item.disabled ? 'cursor-not-allowed opacity-40' : isActive ? 'bg-blue-50' : ''
                    } ${isSelected ? 'font-semibold text-blue-900' : 'text-gray-900'}`}
                  >
                    <ColorSwatchDot background={item.background} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{item.label}</span>
                      {item.note && <span className="block truncate text-xs font-normal text-gray-500">{item.note}</span>}
                    </span>
                    {isSelected && <Check className="h-4 w-4 flex-none text-blue-600" aria-hidden="true" />}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
